import type { Language } from '@/types/translations'

export const LEGACY_BASE_LANGUAGE: Language = 'FR'
export const LEGACY_BASE_LOCALE = 'fr'

export const BUSINESS_LANGUAGES = [
  'FR',
  'NL',
  'EN',
] as const satisfies readonly Language[]

export const CATALOG_LOCALES = ['fr', 'nl', 'en'] as const

export type BusinessLanguage = (typeof BUSINESS_LANGUAGES)[number]
export type CatalogLocale = (typeof CATALOG_LOCALES)[number]

export const BUSINESS_LANGUAGE_OPTIONS: Array<{
  label: string
  value: BusinessLanguage
}> = [
  { label: 'Français', value: 'FR' },
  { label: 'Nederlands', value: 'NL' },
  { label: 'English', value: 'EN' },
]

export const CATALOG_LOCALE_OPTIONS: Array<{
  label: string
  value: CatalogLocale
}> = [
  { label: 'Français', value: 'fr' },
  { label: 'Nederlands', value: 'nl' },
  { label: 'English', value: 'en' },
]

const BUSINESS_LANGUAGE_SET = new Set<string>(BUSINESS_LANGUAGES)
const CATALOG_LOCALE_SET = new Set<string>(CATALOG_LOCALES)

const normalizeString = (value: unknown) =>
  typeof value === 'string' ? value.trim() : undefined

const pushUnique = <T extends string>(items: T[], item: T | undefined) => {
  if (item && !items.includes(item)) items.push(item)
}

const readLanguageValue = (value: unknown) => {
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && 'value' in value) {
    const candidate = (value as { value?: unknown }).value
    return typeof candidate === 'string' ? candidate : undefined
  }
  return undefined
}

export const normalizeBusinessLanguage = (
  value: unknown,
): BusinessLanguage | undefined => {
  const raw = readLanguageValue(value)
  if (!raw) return undefined

  const normalized = raw.trim().toUpperCase()
  return BUSINESS_LANGUAGE_SET.has(normalized)
    ? (normalized as BusinessLanguage)
    : undefined
}

export const normalizeBusinessLanguages = (
  value: unknown,
  fallback: readonly BusinessLanguage[] = [LEGACY_BASE_LANGUAGE],
): BusinessLanguage[] => {
  const source = Array.isArray(value) ? value : fallback
  const normalized: BusinessLanguage[] = []

  for (const item of source) {
    pushUnique(normalized, normalizeBusinessLanguage(item))
  }

  if (normalized.length === 0 && fallback !== source) {
    for (const item of fallback) {
      pushUnique(normalized, normalizeBusinessLanguage(item))
    }
  }

  if (normalized.length) return normalized
  return fallback.length ? [LEGACY_BASE_LANGUAGE] : []
}

export const businessLanguageToCatalogLocale = (
  value: unknown,
): CatalogLocale | undefined => {
  const language = normalizeBusinessLanguage(value)
  if (!language) return undefined

  const locale = language.toLowerCase()
  return CATALOG_LOCALE_SET.has(locale) ? (locale as CatalogLocale) : undefined
}

export const catalogLocaleToBusinessLanguage = (
  value: unknown,
): BusinessLanguage | undefined => {
  const locale = normalizeCatalogLocale(value)
  return locale ? normalizeBusinessLanguage(locale) : undefined
}

export const normalizeCatalogLocale = (
  value: unknown,
): CatalogLocale | undefined => {
  const raw = readLanguageValue(value) ?? normalizeString(value)
  if (!raw) return undefined

  const normalized = raw.trim().toLowerCase()
  return CATALOG_LOCALE_SET.has(normalized)
    ? (normalized as CatalogLocale)
    : undefined
}

export const normalizeCatalogLocales = (
  value: unknown,
  fallback: readonly CatalogLocale[] = [LEGACY_BASE_LOCALE],
): CatalogLocale[] => {
  const source = Array.isArray(value) ? value : fallback
  const normalized: CatalogLocale[] = []

  for (const item of source) {
    pushUnique(normalized, normalizeCatalogLocale(item))
  }

  if (normalized.length === 0 && fallback !== source) {
    for (const item of fallback) {
      pushUnique(normalized, normalizeCatalogLocale(item))
    }
  }

  if (normalized.length) return normalized
  return fallback.length ? [LEGACY_BASE_LOCALE] : []
}

export const businessLanguagesToCatalogLocales = (
  languages: unknown,
  fallback: readonly CatalogLocale[] = [LEGACY_BASE_LOCALE],
): CatalogLocale[] => {
  const locales = (Array.isArray(languages) ? languages : []).flatMap(
    (language) => {
      const locale = businessLanguageToCatalogLocale(language)
      return locale ? [locale] : []
    },
  )

  return normalizeCatalogLocales(locales, fallback)
}

export const resolveCatalogLanguageSettings = ({
  supportedLocales,
  defaultLocale,
  fallbackLocales = [LEGACY_BASE_LOCALE],
}: {
  supportedLocales?: unknown
  defaultLocale?: unknown
  fallbackLocales?: readonly CatalogLocale[]
}) => {
  const normalizedSupportedLocales = normalizeCatalogLocales(
    supportedLocales,
    fallbackLocales,
  )
  const normalizedDefaultLocale =
    normalizeCatalogLocale(defaultLocale) ?? normalizedSupportedLocales[0]
  const resolvedSupportedLocales = [...normalizedSupportedLocales]
  pushUnique(resolvedSupportedLocales, normalizedDefaultLocale)

  return {
    supportedLocales: resolvedSupportedLocales,
    defaultLocale: normalizedDefaultLocale,
  }
}

export const buildContentFallbackLocales = ({
  activeLocale,
  catalogDefaultLocale,
  projectDefaultLocale,
  teamLanguages,
  supportedLocales,
}: {
  activeLocale?: unknown
  catalogDefaultLocale?: unknown
  projectDefaultLocale?: unknown
  teamLanguages?: unknown
  supportedLocales?: unknown
}): CatalogLocale[] => {
  const fallbackLocales: CatalogLocale[] = []

  pushUnique(fallbackLocales, normalizeCatalogLocale(activeLocale))
  pushUnique(fallbackLocales, normalizeCatalogLocale(catalogDefaultLocale))
  pushUnique(fallbackLocales, normalizeCatalogLocale(projectDefaultLocale))

  for (const locale of businessLanguagesToCatalogLocales(teamLanguages, [])) {
    pushUnique(fallbackLocales, locale)
  }

  for (const locale of normalizeCatalogLocales(supportedLocales, [])) {
    pushUnique(fallbackLocales, locale)
  }

  pushUnique(fallbackLocales, LEGACY_BASE_LOCALE)

  return fallbackLocales.length ? fallbackLocales : [LEGACY_BASE_LOCALE]
}
